import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import PlanRatePreview from '@/app/features/utility-plan-selection/PlanRatePreview'
import { adminBillExtractionCollection } from '@/reducers/admin/adminBillExtraction'
import { adminBillExtractionBackfillCollection } from '@/reducers/admin/adminBillExtractionBackfill'
import { adminPlanSeasonalRateSummaryCollection } from '@/reducers/admin/adminPlanSeasonalRateSummary'
import Loader from '../../components/layout/Loader'

const SET_ON_PROFILE_TOOLTIP = (
  <>
    <Typography variant="body2" color="inherit" gutterBottom>
      Whether to write the result onto the profile, or only create the
      extraction.
    </Typography>
    <Typography variant="caption" color="inherit" component="div">
      <strong>Off:</strong> run the bill through Gemini and save the extraction
      + match suggestion only — the profile&apos;s utility/plan are never
      touched.
    </Typography>
    <Typography variant="caption" color="inherit" component="div">
      <strong>On:</strong> also set the matched (or built) utility/plan on the
      profile, subject to the two options below.
    </Typography>
    <Typography
      variant="caption"
      color="inherit"
      component="div"
      sx={{ mt: 0.5 }}
    >
      <em>Scenario:</em> a user uploaded a bill before this feature existed.
      Leave Off to generate the extraction and eyeball the match first; turn On
      once you trust it to actually set their utility/plan.
    </Typography>
  </>
)

const FORCE_OVERWRITE_TOOLTIP = (
  <>
    <Typography variant="body2" color="inherit" gutterBottom>
      Only applies when &quot;Set utility/plan on profile&quot; is on. By
      default we skip profiles that already have BOTH a utility and a plan (so a
      good or manually-set plan isn&apos;t clobbered) — the extraction is still
      created.
    </Typography>
    <Typography variant="caption" color="inherit" component="div">
      <strong>Off:</strong> a profile that already has a utility and a plan is
      left untouched.
    </Typography>
    <Typography variant="caption" color="inherit" component="div">
      <strong>On:</strong> overwrite the existing utility/plan with the
      bill&apos;s result anyway.
    </Typography>
    <Typography
      variant="caption"
      color="inherit"
      component="div"
      sx={{ mt: 0.5 }}
    >
      <em>Scenario:</em> a user is on the wrong plan but already has utility +
      plan set. Normally we&apos;d skip them — check this to force their
      utility/plan to be re-derived from the bill.
    </Typography>
  </>
)

const CREATE_IF_UNMATCHED_TOOLTIP = (
  <>
    <Typography variant="body2" color="inherit" gutterBottom>
      Only applies when &quot;Set utility/plan on profile&quot; is on. When the
      bill doesn&apos;t match BOTH an existing utility and plan in our DB, build
      a custom plan (and a custom utility if needed) from the bill&apos;s rate
      schedule instead of leaving the plan empty.
    </Typography>
    <Typography variant="caption" color="inherit" component="div">
      <strong>Off:</strong> on an incomplete match, attach whatever utility
      matched and leave the plan unset.
    </Typography>
    <Typography variant="caption" color="inherit" component="div">
      <strong>On:</strong> construct a custom plan from the bill&apos;s
      time-of-use rates so the user ends up with a usable plan (needs a rate
      schedule on the bill).
    </Typography>
    <Typography
      variant="caption"
      color="inherit"
      component="div"
      sx={{ mt: 0.5 }}
    >
      <em>Scenario:</em> the bill is from a small co-op we don&apos;t have in
      our DB. With this on, we generate a custom utility + plan from the
      bill&apos;s rates; with it off, the user is left without a plan.
    </Typography>
  </>
)

function CheckboxLabel({ text, tooltip }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>{text}</span>
      <Tooltip
        arrow
        placement="right"
        title={tooltip}
        slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
      >
        <InfoOutlinedIcon
          fontSize="small"
          sx={{ color: 'text.secondary', cursor: 'help' }}
        />
      </Tooltip>
    </Box>
  )
}

function ReferenceCell({ reference }) {
  if (!reference) {
    return <TableCell>—</TableCell>
  }
  return (
    <TableCell>
      {reference.name} (id {reference.id})
      {reference.is_custom && (
        <Chip label="Custom" size="small" color="warning" sx={{ ml: 1 }} />
      )}
    </TableCell>
  )
}

function parseProfileIds(text) {
  return [
    ...new Set(
      text
        .split(/[\s,]+/)
        .map((token) => token.trim())
        .filter(Boolean)
        .map(Number)
        .filter((value) => Number.isInteger(value) && value > 0),
    ),
  ]
}

function ExtractionRow({ label, value }) {
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600, width: '40%' }}>{label}</TableCell>
      <TableCell>
        {value === null || value === undefined || value === ''
          ? '—'
          : String(value)}
      </TableCell>
    </TableRow>
  )
}

export default function BillExtraction({ profileID = '' }) {
  const dispatch = useDispatch()

  const [profileIdsText, setProfileIdsText] = useState(
    profileID ? String(profileID) : '',
  )
  const [setOnProfile, setSetOnProfile] = useState(false)
  const [forceOverwrite, setForceOverwrite] = useState(false)
  const [createIfUnmatched, setCreateIfUnmatched] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  // Current utility/plan + bill + extraction for the selected profile. The endpoint returns one
  // object (keyed by profile_id), so unwrap the single row.
  const {
    data: billExtractionData,
    isLoading: billLoading,
    error: billError,
    refetch: refetchBill,
  } = adminBillExtractionCollection.useFetch({
    params: profileID ? { profile_id: profileID } : undefined,
    require: !!profileID,
    refetchOnChange: true,
  })
  const billExtraction = billExtractionData?.[0]

  // Backfill enqueue is an action/RPC, modeled as the collection's create.
  const {
    createAction,
    data: backfillData,
    isLoading: backfillLoading,
  } = adminBillExtractionBackfillCollection.useCreate({
    path: 'admin/backfill_bill_extraction',
  })
  const backfillResult = backfillData?.[0]

  // Seed the backfill input with the selected profile.
  useEffect(() => {
    if (profileID) {
      setProfileIdsText(String(profileID))
    }
  }, [profileID])

  // Once the profile's current plan is known, pull its TOU rate breakdown for the preview. The
  // admin endpoint resolves any plan id, including the custom plans the bill flow builds.
  const currentPlanId = billExtraction?.plan?.id
  const { data: planRateSummary } =
    adminPlanSeasonalRateSummaryCollection.useFetch(
      `admin/plans/${currentPlanId ?? 0}/seasonal_rate_summary`,
      { require: !!currentPlanId, refetchOnChange: true },
    )

  async function handleEnqueue() {
    const profileIds = parseProfileIds(profileIdsText)
    if (profileIds.length === 0) {
      return
    }
    await dispatch(
      createAction({
        profile_ids: profileIds,
        set_on_profile: setOnProfile,
        force_overwrite: forceOverwrite,
        create_if_unmatched: createIfUnmatched,
      }),
    )
    setShowSuccess(true)
  }

  const isLoading = billLoading || backfillLoading
  const currentUtility = billExtraction?.utility
  const currentPlan = billExtraction?.plan
  const bill = billExtraction?.bill_upload
  const extraction = bill?.extraction
  const parsedIdCount = parseProfileIds(profileIdsText).length

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Bill Extraction & Backfill
      </Typography>

      {/* Section 1: current bill + extraction for the selected profile */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">Current Bill & Extraction</Typography>
            <Button
              variant="outlined"
              onClick={() => refetchBill()}
              disabled={!profileID}
            >
              Refresh
            </Button>
          </Box>

          {!profileID && (
            <Typography color="text.secondary">
              Look up a user above to view their current utility/plan and
              uploaded bill.
            </Typography>
          )}

          {billExtraction && (
            <Box>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ mb: 2 }}
              >
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '40%' }}>
                        Current utility
                      </TableCell>
                      <ReferenceCell reference={currentUtility} />
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '40%' }}>
                        Current plan
                      </TableCell>
                      <ReferenceCell reference={currentPlan} />
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '40%' }}>
                        Plan pending
                      </TableCell>
                      <TableCell>
                        {billExtraction.is_plan_pending ? (
                          <Chip
                            label="Pending — finding plan from bill"
                            size="small"
                            color="warning"
                          />
                        ) : (
                          'No'
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {currentPlan && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Rate preview (current plan)
                  </Typography>
                  {planRateSummary && planRateSummary.length > 0 ? (
                    <PlanRatePreview seasonalRateSummary={planRateSummary} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No rate breakdown available for this plan.
                    </Typography>
                  )}
                </Box>
              )}

              {!bill && (
                <Typography color="text.secondary">
                  No utility bill upload found for this profile.
                </Typography>
              )}

              {bill && (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Bill file:</strong> {bill.file_name || '—'}
                    {bill.s3_url && (
                      <>
                        {'  '}
                        <Link
                          href={bill.s3_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View bill
                        </Link>
                      </>
                    )}
                  </Typography>

                  {!extraction && (
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      No extraction yet. Enqueue a backfill below (leave "Set on
                      profile" off to only create the extraction).
                    </Typography>
                  )}

                  {extraction && (
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ mt: 1 }}
                    >
                      <Table size="small">
                        <TableBody>
                          <ExtractionRow
                            label="Extracted at"
                            value={extraction.created}
                          />
                          <ExtractionRow
                            label="Rate name"
                            value={extraction.rate_name}
                          />
                          <ExtractionRow
                            label="Utility name"
                            value={extraction.utility_name}
                          />
                          <ExtractionRow
                            label="Rate type"
                            value={extraction.rate_type}
                          />
                          <ExtractionRow
                            label="Account number"
                            value={extraction.account_number}
                          />
                          <ExtractionRow
                            label="Has rate schedule"
                            value={extraction.has_rate_schedule ? 'Yes' : 'No'}
                          />
                          <ExtractionRow
                            label="Matched utility"
                            value={
                              extraction.matched_utility_name ||
                              extraction.matched_utility
                            }
                          />
                          <ExtractionRow
                            label="Matched plan"
                            value={
                              extraction.matched_plan_name ||
                              extraction.matched_plan
                            }
                          />
                          <ExtractionRow
                            label="Match confidence"
                            value={extraction.match_confidence}
                          />
                          <ExtractionRow
                            label="Match reason"
                            value={extraction.match_reason}
                          />
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Section 2: enqueue the backfill task */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Enqueue Backfill
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Re-runs extraction + utility/plan matching for each profile's
            uploaded bill. Enter one or more profile IDs (separated by spaces,
            commas, or new lines).
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Profile IDs"
            placeholder="e.g. 12345 67890"
            value={profileIdsText}
            onChange={(event) => setProfileIdsText(event.target.value)}
            sx={{ mb: 1 }}
          />

          <FormGroup sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={setOnProfile}
                  onChange={(event) => setSetOnProfile(event.target.checked)}
                />
              }
              label={
                <CheckboxLabel
                  text="Set utility/plan on profile (otherwise only create the extraction)"
                  tooltip={SET_ON_PROFILE_TOOLTIP}
                />
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={forceOverwrite}
                  onChange={(event) => setForceOverwrite(event.target.checked)}
                  disabled={!setOnProfile}
                />
              }
              label={
                <CheckboxLabel
                  text="Force overwrite even if the profile already has a utility AND plan"
                  tooltip={FORCE_OVERWRITE_TOOLTIP}
                />
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={createIfUnmatched}
                  onChange={(event) =>
                    setCreateIfUnmatched(event.target.checked)
                  }
                  disabled={!setOnProfile}
                />
              }
              label={
                <CheckboxLabel
                  text="Create a custom utility/plan from the bill if the match is incomplete"
                  tooltip={CREATE_IF_UNMATCHED_TOOLTIP}
                />
              }
            />
          </FormGroup>

          <Button
            variant="contained"
            color="primary"
            onClick={handleEnqueue}
            disabled={parsedIdCount === 0 || isLoading}
          >
            Enqueue for {parsedIdCount} profile{parsedIdCount === 1 ? '' : 's'}
          </Button>

          {backfillResult && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success">
                Enqueued: {backfillResult.enqueued?.length ?? 0} · Skipped (no
                bill): {backfillResult.skipped_no_bill?.length ?? 0} · Not
                found: {backfillResult.not_found?.length ?? 0}
              </Alert>
              {backfillResult.skipped_no_bill?.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ mt: 0.5 }}
                >
                  No bill: {backfillResult.skipped_no_bill.join(', ')}
                </Typography>
              )}
              {backfillResult.not_found?.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                >
                  Not found: {backfillResult.not_found.join(', ')}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {billError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load bill data for this profile.
        </Alert>
      )}

      {isLoading && <Loader />}

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          Backfill task enqueued.
        </Alert>
      </Snackbar>
    </Box>
  )
}
